import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { Banknote, Bus, Check, ChevronLeft, Info, Mail, MapPin, Plus, Store } from "lucide-react";
import { accountApi, type Address, type AddressFields } from "../../api/account";
import { orderApi, storeApi, type Cart, type PlaceOrder } from "../../api/cart";
import { ApiError, type Schemas } from "../../api/client";
import { AddressForm, EMPTY_ADDRESS } from "../../components/account/AddressForm";
import { OrderSummary } from "../../components/cart/OrderSummary";
import { quantityUnit } from "../../components/cart/units";
import { LoadError } from "../../components/catalogue/Notices";
import { PageWidth } from "../../components/catalogue/StoreShell";
import { Button, Card, ChoiceCard, Input, Select } from "../../components/ui";
import { manilaNow } from "../../lib/dates";
import type { Fulfilment } from "../../lib/fulfilment";
import { formatPesos } from "../../lib/money";
import { emailProblem } from "../../components/account/EmailField";
import { SESSION_KEY, errorMessage, fieldError, useCustomerSession } from "../account/session";
import { usePageTitle } from "../catalogue/usePageTitle";
import { CART_KEY, useCart } from "./useCart";

const ADDRESSES_KEY = ["account", "addresses"] as const;

/** PRD screen 9: where it goes, how fast, how it's paid, and placing the order. Needs an account (PRD 5.1). */
export function CheckoutRoute() {
  usePageTitle("Checkout");
  const session = useCustomerSession();
  const cart = useCart();
  // Which basket lines the customer ticked on /cart (selective checkout). Absent on a direct
  // hit or after a reload — then we fall back to the whole basket, exactly as before.
  const location = useLocation();
  const itemIds = (location.state as { itemIds?: number[] } | null)?.itemIds;

  if (session.isPending || (session.data?.user && cart.isPending)) return <PageWidth className="py-10 type-body text-text-muted">Loading checkout…</PageWidth>;
  if (!session.data || (session.data.user && !cart.data)) {
    return (
      <PageWidth className="py-10">
        <LoadError onRetry={() => (session.isError ? session.refetch() : cart.refetch())} />
      </PageWidth>
    );
  }
  if (!session.data.user) return <Navigate to="/sign-in" replace state={{ from: "/checkout" }} />;
  if (!cart.data) return <Navigate to="/cart" replace />;
  const chosen = itemIds ? cart.data.items.filter((line) => itemIds.includes(line.id)) : cart.data.items;
  if (chosen.length === 0 || chosen.some((line) => line.problem)) return <Navigate to="/cart" replace />;

  return <Checkout cart={cart.data} itemIds={itemIds} accountEmail={session.data.user.email_address} />;
}

type Contact = { recipient_name: string; phone: string };
type PaymentMethod = PlaceOrder["payment_method"];
/** Step 1 (KALI-24): a saved address, a new one being typed, our counter, or a bus terminal. */
type Destination = number | "new" | "pickup" | "terminal_pickup";
/** Step 2, for an address only. */
type Speed = "delivery" | "urgent_delivery";

function fulfilmentFor(destination: Destination, speed: Speed): Fulfilment {
  return destination === "pickup" || destination === "terminal_pickup" ? destination : speed;
}

function Checkout({ cart, itemIds, accountEmail }: { cart: Cart; itemIds?: number[]; accountEmail: string | null }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Only the ticked lines are ordered and totalled; the rest stay in the basket (selective checkout).
  const selectedLines = itemIds ? cart.items.filter((line) => itemIds.includes(line.id)) : cart.items;
  const subtotalCents = selectedLines.reduce((sum, line) => sum + (line.line_total_cents ?? 0), 0);
  const itemCount = selectedLines.reduce((sum, line) => sum + line.quantity, 0);
  const addresses = useQuery({ queryKey: ADDRESSES_KEY, queryFn: accountApi.addresses });
  const store = useQuery({ queryKey: ["store"], queryFn: storeApi.get, staleTime: Infinity });
  // One key for this visit to checkout: a double tap or a retry finds the same order (design doc §4).
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [picked, setPicked] = useState<Destination | null>(null);
  const [pickedSpeed, setPickedSpeed] = useState<Speed>("delivery");
  const [busLine, setBusLine] = useState("");
  const [busTerminal, setBusTerminal] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("bank_transfer");
  const [collector, setCollector] = useState<Contact | null>(null);
  const [note, setNote] = useState("");
  // Asked only of an account without one, and optional: without it, staff text (PRD 5.6).
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string>();
  const [notice, setNotice] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const saved = addresses.data ?? [];
  const find = (destination: Destination) => (typeof destination === "number" ? (saved.find((address) => address.id === destination) ?? null) : null);
  // The first saved address is picked until the customer picks something else; none saved, the form shows.
  const destination: Destination = picked !== null && (typeof picked !== "number" || find(picked)) ? picked : (saved[0]?.id ?? "new");
  const chosen = find(destination);
  // The same-day rider only reaches listed towns (design doc 7.1); elsewhere it isn't offered.
  const riderReaches = chosen?.urgent_delivery ?? false;
  const speed: Speed = riderReaches ? pickedSpeed : "delivery";
  const fulfilment = fulfilmentFor(destination, speed);
  const toAddress = fulfilment === "delivery" || fulfilment === "urgent_delivery";
  const pickupContact = collector ?? { recipient_name: saved[0]?.recipient_name ?? "", phone: saved[0]?.phone ?? "" };
  // Null when cash on delivery is open for this way of receiving it, or why not (design doc 7.1).
  const codRefusal = cart.cash_on_delivery[fulfilment];
  const payment: PaymentMethod = codRefusal ? "bank_transfer" : paymentMethod;
  const quoted = fulfilment === "delivery" || fulfilment === "terminal_pickup";

  /** Moves step 1 or 2, and says so whenever that changes something the customer already chose. */
  const choose = (nextDestination: Destination, nextSpeed: Speed, address = find(nextDestination)) => {
    const said: string[] = [];
    let settledSpeed = nextSpeed;
    if (address && nextSpeed === "urgent_delivery" && !address.urgent_delivery) {
      settledSpeed = "delivery";
      said.push(`The same-day rider doesn't reach ${address.city}, so we've switched you to courier delivery.`);
    }
    const next = fulfilmentFor(nextDestination, settledSpeed);
    const refusal = cart.cash_on_delivery[next];
    if (payment === "cash_on_delivery" && refusal) {
      setPaymentMethod("bank_transfer");
      said.push(`Cash on delivery isn't possible for ${refusal === "terminal_pickup" ? "bus terminal pickup" : `${SPEED_NAME[next]} on this order`}, so we've switched you to bank transfer.`);
    }
    setPicked(nextDestination);
    setPickedSpeed(settledSpeed);
    setNotice(said.length ? said.join(" ") : null);
  };

  const saveAddress = useMutation({
    mutationFn: (fields: AddressFields) => accountApi.createAddress(fields),
    onSuccess: (address) => {
      queryClient.setQueryData<Address[]>(ADDRESSES_KEY, [...saved, address]);
      choose(address.id, speed, address);
    },
  });

  const place = useMutation({
    mutationFn: (order: PlaceOrder) => orderApi.place(order),
    onSuccess: async (order) => {
      await navigate(`/orders/${order.reference}`, { replace: true, state: { placed: true } });
      queryClient.invalidateQueries({ queryKey: CART_KEY });
      queryClient.invalidateQueries({ queryKey: ["account", "orders"] });
      // An email given here is now the account's: the order page says what reaches it.
      queryClient.invalidateQueries({ queryKey: SESSION_KEY });
    },
    onError: (error) => {
      // The basket moved under the customer: show them today's version before they pay for it.
      if (error instanceof ApiError && (error.code === "prices_changed" || error.code === "cart_changed")) {
        queryClient.invalidateQueries({ queryKey: CART_KEY });
      }
    },
  });

  const submit = () => {
    setProblem(null);
    const contact = toAddress ? chosen : pickupContact;
    if (toAddress && !chosen) return setProblem("Choose a delivery address, or save a new one.");
    if (fulfilment === "terminal_pickup" && (!busLine || !busTerminal.trim())) return setProblem("Choose the bus line and enter the terminal where you'll collect it.");
    if (!contact?.recipient_name.trim() || !contact.phone.trim()) return setProblem("Enter who will collect the order and their mobile number.");
    const typedEmail = accountEmail ? "" : email.trim();
    if (typedEmail && emailProblem(typedEmail)) {
      setEmailError(emailProblem(typedEmail));
      return setProblem("Check the email address, or leave it empty.");
    }

    const address = toAddress && chosen
      ? { street: chosen.street, barangay: chosen.barangay, city: chosen.city, province: chosen.province, postal_code: chosen.postal_code }
      : {};
    place.mutate({
      fulfilment,
      payment_method: payment,
      recipient_name: contact.recipient_name,
      phone: contact.phone,
      ...address,
      ...(fulfilment === "terminal_pickup" ? { bus_line: busLine, bus_terminal: busTerminal.trim() } : {}),
      note,
      ...(typedEmail ? { email_address: typedEmail } : {}),
      idempotency_key: idempotencyKey,
      expected_total_cents: subtotalCents,
      item_ids: selectedLines.map((line) => line.id),
    });
  };

  const serverEmailError = fieldError(place.error, "email_address");
  const placeError = place.error instanceof ApiError && (place.error.code === "cart_changed" || serverEmailError) ? null : errorMessage(place.error);
  // Who hears about this order by email, and who gets a text from staff instead.
  const emailed = Boolean(accountEmail || (email.trim() && !emailProblem(email)));
  const saveErrors = saveAddress.error instanceof ApiError ? saveAddress.error.details : {};
  const blocked = place.isPending || destination === "new";
  const busLines = store.data?.bus_lines ?? [];
  const pickupPoint = store.data?.pickup_point;
  let step = 1;

  const alerts = (
    <>
      {(problem || placeError) && (
        <p role="alert" className="mt-3 mb-0 type-small text-danger-700">
          {problem ?? placeError}
        </p>
      )}
      {place.error instanceof ApiError && place.error.code === "cart_changed" && (
        <p role="alert" className="mt-3 mb-0 type-small text-danger-700">
          Something in your basket can't be ordered any more. <Link to="/cart">Check your basket</Link>.
        </p>
      )}
    </>
  );

  return (
    <PageWidth className="pt-8">
      {/* The phone's tab bar gives way to the Place order bar here, so this is the way back. */}
      <Link to="/cart" className="mb-2 inline-flex min-h-11 items-center gap-1 type-small md:hidden">
        <ChevronLeft size={16} aria-hidden="true" />
        Basket
      </Link>
      <h1 className="mb-6 type-h1 [font-size:var(--text-2xl)]">Checkout</h1>

      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-w-0 flex-col gap-8">
          <fieldset className="m-0 min-w-0 border-0 p-0">
            <StepTitle as="legend" number={step++}>Where should we send it?</StepTitle>
            {addresses.isPending ? (
              <div aria-busy="true" aria-label="Loading your addresses" className="flex flex-col gap-3">
                {[0, 1, 2].map((index) => (
                  <div key={index} className="h-18 rounded-md border border-border-subtle bg-surface-sunken" />
                ))}
              </div>
            ) : addresses.isError ? (
              <LoadError onRetry={() => addresses.refetch()} />
            ) : (
              <div className="flex flex-col gap-3">
                <span className="type-label text-text-body">Deliver to</span>
                {saved.map((address) => (
                  <ChoiceCard key={address.id} name="destination" icon={MapPin} checked={destination === address.id} onChange={() => choose(address.id, pickedSpeed)}>
                    <span className="type-body-strong text-text-strong">
                      {address.recipient_name} · {address.phone}
                    </span>
                    <span className="type-small text-text-muted">
                      {address.street}, {address.barangay}, {address.city}, {address.province}
                      {address.postal_code ? ` ${address.postal_code}` : ""}
                    </span>
                    {address.urgent_delivery && (
                      <span className="mt-0.5 flex items-center gap-1.5 type-small text-green-800">
                        <Check size={14} strokeWidth={2.4} aria-hidden="true" />
                        Same-day rider available
                      </span>
                    )}
                  </ChoiceCard>
                ))}
                {saved.length > 0 && destination !== "new" && (
                  <Button type="button" variant="outline" size="sm" icon={Plus} className="self-start" onClick={() => { saveAddress.reset(); setPicked("new"); setNotice(null); }}>
                    Use a new address
                  </Button>
                )}
                {destination === "new" && (
                  <Card padding="var(--space-6)">
                    <p className="mb-4 type-small text-text-muted">Saved to your account, so next time it's one tap.</p>
                    {/* Its own form, which is why checkout isn't one: forms don't nest. */}
                    <AddressForm
                      initial={EMPTY_ADDRESS}
                      onSave={(fields) => saveAddress.mutate(fields)}
                      onCancel={saved.length > 0 ? () => setPicked(null) : undefined}
                      saving={saveAddress.isPending}
                      errors={saveErrors}
                      error={saveAddress.error && Object.keys(saveErrors).length === 0 ? errorMessage(saveAddress.error) : undefined}
                    />
                  </Card>
                )}

                <span className="mt-3 type-label text-text-body">Or collect it</span>
                <ChoiceCard
                  name="destination"
                  icon={Store}
                  checked={destination === "pickup"}
                  onChange={() => choose("pickup", pickedSpeed)}
                  aside={<span className="type-body-strong text-green-700">Free</span>}
                >
                  <span className="type-body-strong text-text-strong">Collect from us</span>
                  {pickupPoint && (
                    <span className="type-small text-text-muted">
                      {pickupPoint.address} · {pickupPoint.hours}
                    </span>
                  )}
                </ChoiceCard>
                <ChoiceCard
                  name="destination"
                  icon={Bus}
                  checked={destination === "terminal_pickup"}
                  onChange={() => choose("terminal_pickup", pickedSpeed)}
                  aside={<span className="type-body-strong text-text-strong">Quoted before you pay</span>}
                >
                  <span className="type-body-strong text-text-strong">Collect from a bus terminal</span>
                  {busLines.length > 0 && <span className="type-small text-text-muted">{listed(busLines.map((line) => line.name))}</span>}
                  {cart.cash_on_delivery.terminal_pickup && <BankTransferOnly />}
                </ChoiceCard>
              </div>
            )}
          </fieldset>

          {toAddress && chosen && (
            <fieldset className="m-0 min-w-0 border-0 p-0">
              <StepTitle as="legend" number={step++}>How fast?</StepTitle>
              <div className="flex flex-col gap-3">
                <ChoiceCard
                  name="speed"
                  checked={speed === "delivery"}
                  onChange={() => choose(destination, "delivery")}
                  aside={<span className="type-body-strong text-text-strong">Quoted before you pay</span>}
                >
                  <span className="type-body-strong text-text-strong">Courier delivery</span>
                  <span className="type-small text-text-muted">Nationwide, with a tracking number</span>
                  {cart.cash_on_delivery.delivery && <BankTransferOnly />}
                </ChoiceCard>
                {riderReaches && (
                  <ChoiceCard
                    name="speed"
                    checked={speed === "urgent_delivery"}
                    onChange={() => choose(destination, "urgent_delivery")}
                    aside={<span className="type-body-strong text-text-strong">Paid to the rider</span>}
                  >
                    <span className="type-body-strong text-text-strong">{riderWhen(store.data?.urgent_delivery, payment)}</span>
                    <span className="type-small text-text-muted">Same-day rider · Lalamove or Grab</span>
                    {cart.cash_on_delivery.urgent_delivery && <BankTransferOnly />}
                  </ChoiceCard>
                )}
                {!riderReaches && !notice && (
                  <p className="m-0 flex items-start gap-2 type-small text-text-muted">
                    <Info size={16} className="mt-0.5 flex-none" aria-hidden="true" />
                    Same-day rider reaches towns within 120 km of Quezon City.
                  </p>
                )}
              </div>
            </fieldset>
          )}

          {!toAddress && (
            <section>
              <StepTitle number={step++}>{fulfilment === "terminal_pickup" ? "Which terminal?" : "Who's collecting?"}</StepTitle>
              {fulfilment === "terminal_pickup" && (
                <div className="mb-6 grid gap-x-5 gap-y-4 sm:grid-cols-2">
                  <Select
                    label="Bus line"
                    value={busLine}
                    onChange={(event) => setBusLine(event.target.value)}
                    options={[{ value: "", label: "Choose a bus line" }, ...busLines.map((line) => ({ value: line.key, label: line.name }))]}
                    hint={busLines.find((line) => line.key === busLine)?.note ?? undefined}
                  />
                  <Input
                    label="Terminal where you'll collect it"
                    placeholder="e.g. Legazpi terminal"
                    maxLength={200}
                    value={busTerminal}
                    onChange={(event) => setBusTerminal(event.target.value)}
                  />
                </div>
              )}
              {fulfilment === "terminal_pickup" && <p className="mb-3 type-label text-text-strong">Who's collecting</p>}
              <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
                <Input
                  label="Full name"
                  autoComplete="name"
                  value={pickupContact.recipient_name}
                  onChange={(event) => setCollector({ ...pickupContact, recipient_name: event.target.value })}
                />
                <Input
                  label="Mobile number"
                  autoComplete="tel"
                  inputMode="tel"
                  mono
                  placeholder="0917 000 0000"
                  value={pickupContact.phone}
                  onChange={(event) => setCollector({ ...pickupContact, phone: event.target.value })}
                  hint={fulfilment === "pickup" ? "We'll text when it's ready." : undefined}
                />
              </div>
            </section>
          )}

          {notice && (
            <p role="status" className="-mt-4 mb-0 flex items-start gap-2.5 rounded-md bg-surface-tint-blue px-4 py-3 type-small text-blue-800">
              <Info size={18} className="mt-px flex-none" aria-hidden="true" />
              {notice}
            </p>
          )}

          <fieldset className="m-0 min-w-0 border-0 p-0">
            <StepTitle as="legend" number={step++}>Payment</StepTitle>
            <div className="flex flex-col gap-3">
              <ChoiceCard name="payment" checked={payment === "bank_transfer"} onChange={() => { setPaymentMethod("bank_transfer"); setNotice(null); }}>
                <span className="type-body-strong text-text-strong">Bank transfer</span>
                <span className="type-small text-text-muted">
                  {quoted
                    ? `We ${emailed ? "email" : "text"} you the delivery charge first. Then transfer the full total in one payment, and upload a photo of the receipt. We pack your order once we've checked it.`
                    : "After you place the order we show our bank details. Transfer the total, then upload a photo of the receipt. We pack your order once we've checked it."}
                </span>
              </ChoiceCard>
              <ChoiceCard
                name="payment"
                checked={payment === "cash_on_delivery"}
                disabled={codRefusal !== null}
                onChange={() => { setPaymentMethod("cash_on_delivery"); setNotice(null); }}
              >
                <span className={codRefusal ? "type-body-strong text-text-muted" : "type-body-strong text-text-strong"}>
                  {fulfilment === "pickup" ? "Pay when you collect" : "Cash on delivery"}
                </span>
                <span className="type-small text-text-muted">{codRefusal ? COD_REFUSAL[codRefusal] : codHint(fulfilment, emailed)}</span>
              </ChoiceCard>
            </div>
          </fieldset>

          <section>
            <StepTitle number={step++}>Anything we should know?</StepTitle>
            <label className="block">
              <span className="mb-2 block type-label text-text-strong">
                Note for us <span className="type-small font-normal text-text-muted">Optional</span>
              </span>
              <textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                rows={3}
                maxLength={1000}
                className="block w-full resize-y rounded-sm border border-border-default bg-surface-card px-3 py-2 type-body inset-shadow-default focus:border-green-700 focus:outline-none"
              />
              <span className="mt-2 block type-small text-text-muted">Need an official receipt? Put your registered name and TIN here.</span>
            </label>
            {!accountEmail && (
              <div className="mt-6">
                <Input
                  label="Email for order updates"
                  type="email"
                  icon={Mail}
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  error={emailError ?? serverEmailError}
                  hint="Optional. We email the order, the delivery charge and when it's on its way. Leave it empty and we text you instead."
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setEmailError(undefined);
                    setProblem(null);
                  }}
                />
              </div>
            )}
          </section>
        </div>

        <div className="lg:sticky lg:top-24">
          <OrderSummary
            itemCount={itemCount}
            subtotalCents={subtotalCents}
            fulfilment={fulfilment}
            paymentMethod={payment}
            emailed={emailed}
            lines={selectedLines.flatMap((line) =>
              line.listing ? [{ id: line.id, name: line.listing.name, quantity: `${line.quantity} ${quantityUnit(line.listing.unit, line.quantity)}`, totalCents: line.line_total_cents ?? 0 }] : [],
            )}
          >
            {/* Below desktop width the pinned bar carries the button instead. */}
            <div className="hidden lg:block">
              <Button size="lg" fullWidth icon={Check} onClick={submit} disabled={blocked}>
                {place.isPending ? "Placing your order…" : "Place order"}
              </Button>
              {alerts}
            </div>
            <p className="mt-4 mb-0 type-small text-text-muted">
              {!emailed ? "No email: we text you about this order instead." : payment === "cash_on_delivery" ? "We email you the order." : "We email you the order and our bank details."}
            </p>
          </OrderSummary>
        </div>
      </div>

      {/* Phone and tablet: the total and the button never scroll out of view (KALI-24). */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border-subtle bg-surface-card px-4 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))] shadow-[0_-4px_12px_rgba(36,35,31,.09)] lg:hidden">
        {alerts}
        <div className="mt-0 flex items-center gap-3">
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="type-small text-text-muted">
              Total <span className="type-price [font-size:var(--text-lg)] text-text-strong">{formatPesos(subtotalCents)}</span>
            </span>
            {quoted && <span className="type-small [font-size:var(--text-xs)] text-text-muted">plus delivery, quoted before you pay</span>}
            {fulfilment === "urgent_delivery" && <span className="type-small [font-size:var(--text-xs)] text-text-muted">plus the rider's fee, paid to them</span>}
          </div>
          <Button size="lg" onClick={submit} disabled={blocked} className="flex-none">
            {place.isPending ? "Placing…" : "Place order"}
          </Button>
        </div>
      </div>
    </PageWidth>
  );
}

/** Today if ready before the cutoff on a working day, otherwise the next working day (UrgentDelivery on the server). */
function riderWhen(urgent: Schemas["Store"]["urgent_delivery"] | undefined, payment: PaymentMethod) {
  const now = manilaNow();
  const cutoff = urgent?.cutoff_hour ?? 15;
  const workingDay = !now.sunday && !(urgent?.holidays ?? []).includes(now.date);
  if (!workingDay || now.hour >= cutoff) return "Next working day";
  const time = `${cutoff > 12 ? cutoff - 12 : cutoff} ${cutoff >= 12 ? "PM" : "AM"}`;
  // Cash on delivery is ready once placed; a bank transfer once we confirm the payment.
  return payment === "cash_on_delivery" ? `Today if placed by ${time}` : `Today if paid by ${time}`;
}

/** "Victory Liner, DLTB, Super Lines or Partas" */
function listed(names: string[]) {
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}` : (names[0] ?? "");
}

function BankTransferOnly() {
  return (
    <span className="mt-1 flex items-center gap-1.5 self-start rounded-pill bg-surface-sunken px-2 py-0.5 type-small text-text-body">
      <Banknote size={14} aria-hidden="true" />
      Bank transfer only
    </span>
  );
}

const SPEED_NAME: Record<Fulfilment, string> = {
  delivery: "courier delivery",
  urgent_delivery: "the same-day rider",
  terminal_pickup: "bus terminal pickup",
  pickup: "collecting from us",
};

function codHint(fulfilment: Fulfilment, emailed: boolean): string {
  return {
    delivery: `Pay the courier when it arrives: the parts and the delivery charge, which we ${emailed ? "email" : "text"} you first. You can cancel if you don't agree to it.`,
    urgent_delivery: "Pay the rider for the parts, and their fee, when it arrives. Up to ₱5,000 of parts.",
    terminal_pickup: "",
    pickup: "Pay at our counter when you collect it.",
  }[fulfilment];
}

/** Why cash on delivery isn't open (CashOnDelivery::REASONS on the server). */
const COD_REFUSAL: Record<NonNullable<Cart["cash_on_delivery"][Fulfilment]>, string> = {
  terminal_pickup: "Not for bus terminal pickup: the bus lines don't collect cash.",
  corporate: "Company accounts pay by bank transfer.",
  blocked: "Not available on your account.",
  weight_missing: "Not for this basket yet: we don't have the weight of every part. Ask us if you need it.",
  heavy_freight: "Not for orders of 45 kg or more: they go by freight, which doesn't collect cash.",
  over_urgent_limit: "Urgent delivery collects cash for up to ₱5,000 of parts. Choose standard delivery for cash on delivery.",
};

function StepTitle({ number, as: Tag = "h2", children }: { number: number; as?: "h2" | "legend"; children: ReactNode }) {
  return (
    <Tag className="mb-4 flex items-center gap-3 p-0 type-h3 [font-size:var(--text-lg)]">
      <span aria-hidden="true" className="flex size-7 flex-none items-center justify-center rounded-full bg-green-700 type-sku [font-size:var(--text-sm)] text-white">
        {number}
      </span>
      {children}
    </Tag>
  );
}
